<template>
  <StandardPageTemplate
    :back-back-name="licenseId ? 'Licenses' : null"
    :back-back-route="licenseId ? { name: 'ProviderLicenseIndex' } : null"
    :back-name="licenseId ? 'License' : 'Licenses'"
    :back-route="backRoute"
    :processing="processing"
    :title="workflowInstance?.name"
  >
    <v-progress-linear
      v-if="processing"
      indeterminate
    />

    <div
      v-if="workflowInstance"
      class="d-flex flex-column ga-4"
    >
      <StandardCard
        card-text-class="pa-3"
        no-title
      >
        <StandardChip
          v-if="workflowInstance.status"
          :text="workflowInstance.status"
          role="status"
          capitalize
        />
      </StandardCard>

      <LicenseWorkflowPhases
        @reload="load"
        @review-task="openTaskDetail"
        @workflow-instance-updated="handleWorkflowInstanceUpdated"
        :license-id="licenseId"
        :workflow-instance="workflowInstance"
      />
    </div>

    <TaskDetailDialog
      v-if="selectedTask"
      @close="closeTaskDetail"
      @updated="handleTaskUpdated"
      :open="taskDetailOpen"
      :task="selectedTask"
    />
  </StandardPageTemplate>
</template>

<script setup>
import { useRoute } from 'vue-router';

import LicenseWorkflowPhases from '@/shared/components/licenses/LicenseWorkflowPhases.vue';
import TaskDetailDialog from '@/shared/components/tasks/TaskDetailDialog.vue';
import useApi from '@/shared/composables/useApi';

const { api: Api } = useApi();
const route = useRoute();

const processing = ref(true);
const selectedTask = ref(null);
const taskDetailOpen = ref(false);
const workflowInstance = ref(null);

const licenseId = computed(() => {
  const artifact = workflowInstance.value?.workflow_artifacts?.find(
    (workflowArtifact) => workflowArtifact.artifact_type === 'License',
  );

  return artifact?.artifact_id ?? route.query.license_id ?? null;
});

const backRoute = computed(() => {
  if (licenseId.value) {
    return {
      name: 'LicenseShow',
      params: { id: licenseId.value },
      query: { tab: 'workflows' },
    };
  }

  return { name: 'ProviderLicenseIndex' };
});

async function closeTaskDetail() {
  taskDetailOpen.value = false;
  selectedTask.value = null;
  await load();
}

async function handleTaskUpdated() {
  taskDetailOpen.value = false;
  selectedTask.value = null;
  await load();
}

function handleWorkflowInstanceUpdated(updated) {
  if (!updated) return;

  workflowInstance.value = {
    ...(workflowInstance.value ?? {}),
    ...updated,
  };
}

async function load() {
  processing.value = true;

  const response = await Api.manager.workflowInstance.get(route.params.workflowInstanceId);
  workflowInstance.value = response?.data;
  processing.value = false;
}

function providerIdFromWorkflowInstance(workflowInstance) {
  return (
    workflowInstance?.workflow_artifacts?.find((artifact) => artifact.artifact_type === 'Provider')
      ?.artifact_id ?? null
  );
}

function openTaskDetail(task) {
  selectedTask.value = {
    ...task,
    workflow_artifact: task.workflow_artifact ?? null,
    workflow_artifact_template: task.workflow_artifact_template ?? null,
    provider_id: task.provider_id ?? providerIdFromWorkflowInstance(workflowInstance.value),
    workflow_instance_id: workflowInstance.value.id,
    workflow_name: workflowInstance.value.name,
  };
  taskDetailOpen.value = true;
}

onMounted(load);
</script>
